import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import gibletLogo from '@/assets/giblet-logo.png';

export default function DemoCallForKnowledgeBounty() {
  const navigate = useNavigate();
  const location = useLocation();

  const connectedToCfS = location.state?.connectedToCfS || false;
  const cfsBounty = location.state?.selectedCfSBounty || 0;
  const cfsTitle = location.state?.selectedCfSTitle || '';

  const creationTeamShare = cfsBounty * 0.67;

  const [standaloneBounty, setStandaloneBounty] = useState<string>('5000');
  const [hasBounty, setHasBounty] = useState<string>(connectedToCfS ? 'yes' : '');
  const [creatorPct, setCreatorPct] = useState('50');
  const [contributorPct, setContributorPct] = useState('45');
  const [escrowCondition, setEscrowCondition] = useState('');

  const confirmerPct = 5; // Giblet locked
  const bountyAmount = connectedToCfS ? creationTeamShare : Number(standaloneBounty) || 0;
  const creatorAmount = bountyAmount * (Number(creatorPct) / 100);
  const contributorAmount = bountyAmount * (Number(contributorPct) / 100);
  const confirmerAmount = bountyAmount * (confirmerPct / 100);

  const handleNext = () => {
    navigate('/demo-call-for-knowledge/details', {
      state: {
        ...location.state,
        hasBounty: hasBounty === 'yes',
        bountyAmount,
        creatorPct: Number(creatorPct),
        contributorPct: Number(contributorPct),
        confirmerPct,
        escrowCondition,
      }
    });
  };

  const isNextDisabled = !connectedToCfS && !hasBounty;

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
      <div className="max-w-2xl w-full space-y-8">
        <div className="flex justify-center mb-8">
          <img src={gibletLogo} alt="Giblet Logo" className="h-16 w-auto" />
        </div>

        <div className="text-center space-y-4">
          <h1 className="text-3xl font-bold text-foreground">Knowledge Bounty Configuration</h1>
          <p className="text-lg text-muted-foreground">
            {connectedToCfS
              ? `Configure bounty distribution from "${cfsTitle}"`
              : 'Set up the bounty for this standalone Call for Knowledge'}
          </p>
        </div>

        <div className="bg-card rounded-lg p-8 shadow-lg space-y-6">
          {connectedToCfS ? (
            <div className="space-y-4">
              <div className="bg-primary/10 rounded-lg p-4 space-y-2">
                <h3 className="font-semibold text-foreground">Connected CfS Bounty</h3>
                <div className="text-sm text-muted-foreground">
                  Total Solution Bounty: <span className="font-bold text-foreground">${cfsBounty.toLocaleString()}</span>
                </div>
                <div className="text-sm text-muted-foreground">
                  Creation Team Share (67%): <span className="font-bold text-foreground">${creationTeamShare.toLocaleString()}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  The 67% Creation Team share (${creationTeamShare.toLocaleString()}) from the CfS bounty will be
                  distributed to the CfK team upon successful knowledge base completion.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <RadioGroup value={hasBounty} onValueChange={setHasBounty}>
                <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-accent/50 transition-colors">
                  <RadioGroupItem value="yes" id="bounty-yes" />
                  <Label htmlFor="bounty-yes" className="cursor-pointer">
                    <div className="font-semibold text-foreground">Yes, attach a Knowledge Bounty</div>
                  </Label>
                </div>
                <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-accent/50 transition-colors">
                  <RadioGroupItem value="no" id="bounty-no" />
                  <Label htmlFor="bounty-no" className="cursor-pointer">
                    <div className="font-semibold text-foreground">No bounty — standard attribution only</div>
                  </Label>
                </div>
              </RadioGroup>

              {hasBounty === 'yes' && (
                <div className="space-y-2 pt-2 border-t border-border">
                  <Label>Knowledge Bounty Amount (USD)</Label>
                  <Input
                    type="number"
                    value={standaloneBounty}
                    onChange={(e) => setStandaloneBounty(e.target.value)}
                    placeholder="Enter amount"
                  />
                </div>
              )}
            </div>
          )}

          {(connectedToCfS || hasBounty === 'yes') && (
            <>
              <div className="border-t border-border pt-4 space-y-4">
                <h3 className="font-semibold text-foreground">Team Role Distribution</h3>
                <p className="text-sm text-muted-foreground">
                  Allocate the bounty across the CfK team. Confirmer (Giblet) is locked at 5%.
                </p>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs">Creator %</Label>
                    <Input value={creatorPct} onChange={(e) => setCreatorPct(e.target.value)} type="number" />
                    <div className="text-xs text-muted-foreground">${creatorAmount.toLocaleString()}</div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Contributor %</Label>
                    <Input value={contributorPct} onChange={(e) => setContributorPct(e.target.value)} type="number" />
                    <div className="text-xs text-muted-foreground">${contributorAmount.toLocaleString()}</div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Confirmer (Giblet)</Label>
                    <Input value="5" disabled type="number" />
                    <div className="text-xs text-muted-foreground">${confirmerAmount.toLocaleString()}</div>
                  </div>
                </div>
              </div>

              <div className="border-t border-border pt-4 space-y-2">
                <Label>Escrow Release Condition</Label>
                <Select value={escrowCondition} onValueChange={setEscrowCondition}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select release condition..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kb-confirmed">KB confirmed by Confirmer</SelectItem>
                    <SelectItem value="kb-minted">KB minted on-chain</SelectItem>
                    <SelectItem value="milestone">Milestone-based release</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>

        <div className="flex justify-center gap-4">
          <Button variant="outline" onClick={() => navigate('/demo-call-for-knowledge/connection')} className="min-w-[120px]">
            Back
          </Button>
          <Button onClick={handleNext} disabled={isNextDisabled} className="min-w-[120px]">
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
